// The decisions behind the Player Databases page, with the DOM left out.
//
// js/ui/player-db-page.js is one long file of rendering and event wiring, and the
// rules it encodes — which column a table sorts on, what an import line means,
// which players a report would actually move, what a rating-list form saves when
// its boxes are left blank — were only reachable by clicking. They live here
// instead: plain values in, plain values out, so tests/player-db-logic.test.js can
// state each rule once and the UI file is left doing what it is for.
//
// No DOM, no Firestore, no state.js. The global tr() is used for the few strings
// that ARE the result (a warning, an error message, a card's caption); tests shim
// it the way tests/helpers/browser-shims.js shims the other browser globals.
//
// Shape helpers that the service layer also needs (player name, search key,
// search semantics, event ids, list lookup) stay in js/utils/player-db-shape.js;
// rating resolution stays in js/utils/player-ratings.js. Both are imported here
// rather than re-implemented.

import { formatDbPlayerName, searchPlayers, findRatingList } from './player-db-shape.js';
import { readRatings, gamesInList, highestKnownRating } from './player-ratings.js';

// ---------------------------------------------------------------------------
// The database list pane
// ---------------------------------------------------------------------------

/**
 * Split the registry into the three kinds of card it shows.
 *
 * Invitations come first because they want an answer, and headings only earn
 * their place once there is more than one kind to tell apart — an arbiter with
 * nothing but their own databases sees the plain grid.
 */
export function groupDatabases(databases) {
    const list = Array.isArray(databases) ? databases : [];
    const owned = list.filter(d => d && d.role === 'owner');
    const shared = list.filter(d => d && d.role === 'arbiter');
    const invitations = list.filter(d => d && d.role === 'pending');
    return {
        owned,
        shared,
        invitations,
        withHeadings: shared.length > 0 || invitations.length > 0
    };
}

/** The "players only / 1 rating list / N rating lists" caption on a card. */
export function ratingListsSummary(count) {
    const n = Number(count) || 0;
    if (n === 0) return tr('players only');
    if (n === 1) return tr('1 rating list');
    return n + ' ' + tr('rating lists');
}

/**
 * Who else is involved with a database: whose it is, on one shared WITH you; how
 * many arbiters it is shared with, on one of your own. `kind` picks the icon.
 */
export function databaseCardSharing(database) {
    const d = database || {};
    if (d.role === 'arbiter') {
        return { kind: 'owner', text: tr('Owner: {email}', { email: d.ownerEmail || '' }) };
    }
    const count = (d.cooperatingArbiters || []).length;
    if (count === 0) return { kind: '', text: '' };
    return {
        kind: 'shared',
        text: count === 1 ? tr('Shared with 1 arbiter') : tr('Shared with {count} arbiters', { count })
    };
}

/**
 * The arbiter's databases and their rating lists, for the tournament settings
 * picker. Pending invitations are dropped (they cannot be read yet), but a
 * database with NO rating lists is kept: binding one is how an unrated register
 * of players is attached to a tournament.
 */
export function toSelectableDatabases(databases) {
    return (Array.isArray(databases) ? databases : [])
        .filter(d => d && d.role !== 'pending')
        .map(d => ({
            dbId: d.id,
            dbName: d.name || tr('Untitled'),
            lists: (Array.isArray(d.lists) ? d.lists : [])
                .map(l => ({ listId: l.id, listName: l.name || l.id }))
        }));
}

/** The display name of one rating list inside a database. */
export function ratingListName(database, listId) {
    const list = findRatingList(database, listId);
    return list ? (list.name || listId) : listId;
}

// ---------------------------------------------------------------------------
// The player table: which list the rating columns show, sorting, filtering
// ---------------------------------------------------------------------------

/**
 * Which rating list the Rating and Games columns should show.
 *
 * A <select> with no explicit value displays its FIRST option, so a remembered id
 * that is not among the lists has to fall back to that same first list — otherwise
 * the table reads one list while the picker displays another, and every rating
 * shows as "-". Also the default for a freshly opened database.
 */
export function resolveRatingColumn(lists, currentId) {
    const options = Array.isArray(lists) ? lists : [];
    if (options.some(l => l && String(l.id) === String(currentId))) return currentId;
    return options.length > 0 ? options[0].id : '';
}

/** The rating shown in the table's Rating column for one player. */
export function playerRatingValue(player, listId) {
    return listId ? (readRatings(player)[listId] || 0) : 0;
}

/**
 * The value one player sorts by in one column.
 *
 * Rating and Games follow the list picked in "Show rating", so what they sort on
 * is that list's entry rather than a field on the player. A player with no entry
 * in that list sorts as 0 — including one who holds a legacy FIDE rating under
 * the same key, which is what the table's own "-" already says about them.
 */
export function playerSortValue(player, column, ratingListId) {
    const p = player || {};
    if (column === 'rating' || column === 'games') {
        const entry = (p.ratings || {})[ratingListId];
        if (!entry) return 0;
        // gamesInList() is the shared reader for this entry shape; it coerces a
        // numeric string to a number, which the raw field never is in practice.
        if (column === 'games') return gamesInList(p, ratingListId);
        return readRatings(p)[ratingListId] || 0;
    }
    if (column === 'birthYear') return parseInt(p.birthYear, 10) || 0;
    return (p[column] || '').toString().toLowerCase();
}

/**
 * Sort a copy of the rows. Ties fall back to the name, so a sort on club or title
 * still lands the players inside each block in a readable order.
 */
export function sortDbPlayers(rows, sort, ratingListId) {
    const { column, direction } = sort || {};
    const dir = direction === 'asc' ? 1 : -1;
    return (Array.isArray(rows) ? rows : []).slice().sort((a, b) => {
        const valA = playerSortValue(a, column, ratingListId);
        const valB = playerSortValue(b, column, ratingListId);
        if (valA < valB) return -dir;
        if (valA > valB) return dir;
        return ((a && a.name) || '').localeCompare((b && b.name) || '');
    });
}

/**
 * The rows the table should show: the search applied (same token semantics as
 * everywhere else — see searchPlayers in player-db-shape.js), then the sort.
 *
 * The 500 cap is this table's own: it is a page of a club register, not the
 * 15-row dropdown the Add Player search fills.
 */
export const PLAYER_TABLE_LIMIT = 500;

export function visiblePlayers(players, { filter = '', sort = null, ratingListId = '' } = {}) {
    const all = Array.isArray(players) ? players : [];
    const rows = filter ? searchPlayers(all, filter, PLAYER_TABLE_LIMIT) : all;
    return sortDbPlayers(rows, sort, ratingListId);
}

/**
 * Clicking a header sorts on it; clicking the one already sorted reverses it —
 * the same contract as the roster table (handlePlayerSort in js/ui/players.js).
 */
export function nextSortState(current, column) {
    const now = current || {};
    if (now.column === column) {
        return { column, direction: now.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { column, direction: 'asc' };
}

/** The Remix icon a sortable header shows for its current state. */
export function sortIconClass(isActive, direction) {
    if (!isActive) return 'ri-arrow-up-down-line';
    return direction === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc';
}

// ---------------------------------------------------------------------------
// Rating lists
// ---------------------------------------------------------------------------

/** "K40 ≤ 1500, K20 ≤ 9999" — the K table as one line under a list's name. */
export function describeKBands(kTable) {
    return (Array.isArray(kTable) ? kTable : [])
        .map(b => `K${(b || {}).k} ≤ ${(b || {}).maxRating}`)
        .join(', ');
}

// Mirrors the engine's own defaults (js/core/rating-engine.js DEFAULTS) for the
// four boxes that show their default as a placeholder instead of a value. Kept
// here rather than read off a normalized config because the point is precisely
// to know what "not set" looks like, which a normalized config has already lost.
// These must match the placeholder text in index.html's rating-list-modal.
export const RL_DEFAULTS = Object.freeze({
    startRating: 1000,
    provisionalGames: 0,
    juniorMaxAge: 18,
    maxChangePerEvent: 0
});

/**
 * What a number box should DISPLAY when its default is also the suggestion.
 *
 * A box reading "0" looks like a deliberate choice the arbiter made; an empty box
 * with a faint 0 behind it reads as "this is what happens if you leave it alone" —
 * which is what these fields mean. So the default comes back as ''.
 */
export function suggestedNumberValue(value, suggestion) {
    const isDefault = value === undefined || value === null || value === ''
        || Number(value) === suggestion;
    return isDefault ? '' : value;
}

/** A box where blank means "no value at all" (K factors): '' -> null. */
export function blankOrNumber(raw) {
    const text = String(raw === undefined || raw === null ? '' : raw).trim();
    return text === '' ? null : (parseFloat(text) || 0);
}

/**
 * A box where blank means "use the suggestion the placeholder showed" — the
 * opposite of parseInt('') || 0, which would silently turn an untouched
 * start-rating box into 0 (every new player unrated).
 */
export function numberOrDefault(raw, fallback) {
    const text = String(raw === undefined || raw === null ? '' : raw).trim();
    if (text === '') return fallback;
    const n = parseInt(text, 10);
    return Number.isFinite(n) ? n : fallback;
}

/** One K-band row: an open-ended band and a K of 0 are the forgiving defaults. */
export function parseKBand(maxRating, k) {
    return {
        maxRating: parseInt(maxRating, 10) || 9999,
        k: parseFloat(k) || 0
    };
}

/**
 * The rating-list document the configuration form saves.
 *
 * `previous` is the list as currently stored: rating floor and ceiling no longer
 * have inputs (the LIMITS section is gone), but a list that already carries them
 * must keep them — dropping the fields from the form is a UI change, not a licence
 * to reset saved configuration.
 */
export function buildRatingListConfig(form, previous) {
    const f = form || {};
    return {
        id: f.id || undefined,
        name: String(f.name || '').trim(),
        engine: 'elo',
        startRating: numberOrDefault(f.startRating, RL_DEFAULTS.startRating),
        kTable: Array.isArray(f.kTable) ? f.kTable : [],
        kProvisional: blankOrNumber(f.kProvisional),
        provisional: {
            games: numberOrDefault(f.provisionalGames, RL_DEFAULTS.provisionalGames),
            mode: f.provisionalMode
        },
        kJunior: blankOrNumber(f.kJunior),
        juniorMaxAge: numberOrDefault(f.juniorMaxAge, RL_DEFAULTS.juniorMaxAge),
        ratingFloor: (previous && parseInt(previous.ratingFloor, 10)) || 0,
        ratingCeiling: (previous && parseInt(previous.ratingCeiling, 10)) || 0,
        maxChangePerEvent: numberOrDefault(f.maxChangePerEvent, RL_DEFAULTS.maxChangePerEvent),
        rounding: f.rounding,
        expectedScoreModel: f.expectedScoreModel,
        unratedOpponents: f.unratedOpponents,
        seedUnrated: f.seedUnrated,
        use400Rule: !!f.use400Rule,
        countForfeits: !!f.countForfeits
    };
}

// ---------------------------------------------------------------------------
// The player modal
// ---------------------------------------------------------------------------

/** Name and description as they are saved; a database must have a name. */
export function validateDatabaseSettings(input) {
    const patch = {
        name: String((input && input.name) || '').trim(),
        description: String((input && input.description) || '').trim()
    };
    return { valid: !!patch.name, patch };
}

/**
 * What the opening-rating box for one list should offer.
 *
 * `rated` is truthy only when the player already holds a rating in that list, and
 * then the box is shown read-only: a rating that exists moves only by reporting a
 * tournament (or reverting one). Otherwise the caller fills in the list's start
 * rating, which is what "start rating" means to the arbiter who set it.
 */
export function describeOpeningRating(list, existingRatings) {
    const l = list || {};
    const entry = (existingRatings || {})[l.id];
    const rated = entry && (typeof entry === 'object' ? entry.value : entry);
    return {
        listId: l.id,
        label: l.name || l.id,
        rated: rated || 0,
        games: (entry && typeof entry === 'object' && entry.games) || 0
    };
}

/**
 * The opening ratings a save should carry, from the boxes the arbiter could edit.
 *
 * A blank or non-positive box deliberately leaves the player unrated in that list,
 * and only lists they are not yet rated in ever reach here — the rest are
 * disabled, which is also what the security rules enforce.
 */
export function buildOpeningRatings(entries, now = Date.now()) {
    const ratings = {};
    (Array.isArray(entries) ? entries : []).forEach(entry => {
        const value = parseInt((entry || {}).value, 10);
        if (Number.isFinite(value) && value > 0) {
            ratings[entry.listId] = { value, games: 0, updatedAt: now };
        }
    });
    return ratings;
}

/**
 * The number an opening-rating box should show after a FIDE search result is
 * picked: the highest rating that player is known to hold anywhere, so a titled
 * player does not enter the club list as a beginner — falling back to the list's
 * own start rating for someone who is unrated everywhere.
 */
export function openingRatingForPick(fidePlayer, startRating) {
    const best = highestKnownRating(fidePlayer);
    return best > 0 ? best : (startRating || '');
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

/**
 * One CSV line -> a player, or null for a blank/header row. Deliberately
 * forgiving: a club's exported list rarely matches a column order exactly, so
 * anything missing simply comes through empty rather than failing the import.
 */
export function parseCsvLine(line) {
    const cols = String(line === undefined || line === null ? '' : line)
        .split(/[,;\t]/).map(c => c.trim().replace(/^"|"$/g, ''));
    if (cols.length === 0 || !cols[0]) return null;
    // A header row: the first cell is not a name but a column label.
    if (/^(last\s*name|etternavn|surname|navn|name)$/i.test(cols[0])) return null;
    return {
        lastName: cols[0] || '',
        givenName: cols[1] || '',
        club: cols[2] || '',
        fed: cols[3] || '',
        birthYear: cols[4] || '',
        fideId: cols[5] || '',
        rating: parseInt(cols[6], 10) || 0
    };
}

/** A whole pasted block -> the rows worth importing. */
export function parseImportText(text) {
    return String(text === undefined || text === null ? '' : text)
        .split('\n').map(parseCsvLine).filter(Boolean);
}

/**
 * The rows for an import from the active tournament: each player opens at the
 * highest rating they hold anywhere, so the database records their strength
 * rather than whichever rating this particular event is played at.
 */
export function tournamentPlayersToRows(players) {
    return (Array.isArray(players) ? players : []).map(p => ({
        lastName: p.lastName || '',
        givenName: p.givenName || '',
        club: p.club || '',
        fed: p.fed || '',
        birthYear: p.birthYear || '',
        fideId: p.fideId || '',
        nsfId: p.nsfId || '',
        rating: highestKnownRating(p)
    }));
}

/**
 * Decide what an import would write, before anything is written.
 *
 * Anyone already present under the same name is skipped, so re-running an import
 * tops the list up instead of duplicating it — and a name repeated inside the
 * pasted block itself only lands once. Rows that carry no rating of their own
 * start at the list's start rating, the same as a player added by hand: an import
 * must not silently produce a database full of unrated players.
 *
 * @returns {{ creates: object[], skipped: number }} `creates` are ready to send to
 *   PlayerDB.createPlayer; a failed write is the caller's to count as skipped.
 */
export function planImport(rows, existingPlayers, { listId = '', startRating = 0, now = Date.now() } = {}) {
    const taken = new Set((Array.isArray(existingPlayers) ? existingPlayers : [])
        .map(p => ((p && p.name) || '').toLowerCase()));

    const creates = [];
    let skipped = 0;

    (Array.isArray(rows) ? rows : []).forEach(row => {
        const name = formatDbPlayerName(row.lastName, row.givenName).toLowerCase();
        if (!name || taken.has(name)) { skipped++; return; }
        taken.add(name);

        const ratings = {};
        if (listId) {
            const value = row.rating > 0 ? row.rating : startRating;
            if (value > 0) ratings[listId] = { value, games: 0, updatedAt: now };
        }
        creates.push({ ...row, ratings });
    });

    return { creates, skipped };
}

// ---------------------------------------------------------------------------
// Report preview
// ---------------------------------------------------------------------------

// How strongly one roster player claims one database record, strongest first.
// Claims are settled a strength at a time (see matchTournamentPlayers).
const CLAIM = Object.freeze({
    LINK_CONFIRMED: 0,  // the stored link, and the FIDE ids or the whole names agree
    FIDE_ID: 1,
    LINK_PLAUSIBLE: 2,  // the stored link, and the names share at least one word
    NAME: 3             // the exact name, which is all most club players have
});

// The words of a name, so a stored link can be checked against the record it
// points at: case, order, commas, hyphens and a repeated word do not matter.
// "Hansen, Ola", "Ola Hansen" and "Ola-Hansen" are the same three letters
// apart; "Ola Ola" — what a record with only a given name came out as when
// picked into a tournament — is the record "Ola".
function nameWords(name) {
    return new Set(String(name || '').normalize('NFC').toLowerCase()
        .split(/[\s,-]+/).filter(Boolean));
}

function sameWords(a, b) {
    return a.size > 0 && a.size === b.size && [...a].every(w => b.has(w));
}

function sharesWord(a, b) {
    return [...a].some(w => b.has(w));
}

/**
 * How far a stored link can be believed: CONFIRMED when the FIDE ids agree or
 * the names are the same words, PLAUSIBLE when the names share a word (a
 * corrected spelling, a last name added later), and not at all — null — when
 * the two have nothing in common or carry different FIDE ids.
 */
function linkStrength(fideId, words, record) {
    const recordFideId = record.fideId ? String(record.fideId) : '';
    if (fideId && recordFideId) return fideId === recordFideId ? CLAIM.LINK_CONFIRMED : null;
    const recordWords = nameWords(record.name);
    if (sameWords(words, recordWords)) return CLAIM.LINK_CONFIRMED;
    if (sharesWord(words, recordWords)) return CLAIM.LINK_PLAUSIBLE;
    return null;
}

/**
 * Match the tournament's players to database records.
 *
 * By the database link stored on the roster player (set when they were picked
 * out of the database), then by FIDE id, then by name — an id is unambiguous, a
 * name is the fallback for the club players who have no id (which is the entire
 * reason this feature exists).
 *
 * ONE RECORD, ONE PLAYER. Two roster players resolving to the same record is
 * never right: the engine files both players' games under that one id, the
 * preview lists the record once per claimant with all of their games each, and
 * the server refuses the whole report as 'duplicate-player'. It happened in a
 * live tournament because the Add Player form carried the last search pick's
 * link into players typed in by hand and players edited after it (fixed in
 * js/app.js and openEditPlayerModal), and rosters saved before that fix still
 * hold such links. So:
 *
 *  - A link is believed only while the player still looks like the record: the
 *    same FIDE id, or at least one name in common. A link to someone with
 *    nothing in common is one the form carried over, and is ignored — that
 *    player is matched like one who never had a link.
 *  - Claims are settled strongest first (see CLAIM), and a record two players
 *    claim with equal strength goes to neither: there is no telling which of
 *    them it is. A record taken or tied at one strength is out of reach at the
 *    weaker ones.
 *
 * @returns {{ matched: {tournamentPlayer, dbPlayer}[], unmatched: object[],
 *             shared: object[], idMap: Map }}
 *   `unmatched` are players with no record at all. `shared` are players whose
 *   only record went to someone else in this tournament, or to nobody because
 *   two claimed it alike; they are skipped too, but "not in this database"
 *   would be untrue of them, so they are kept apart. `idMap` maps tournament
 *   player id -> database player id, one to one, so the deltas the engine
 *   produces address database documents directly.
 */
export function matchTournamentPlayers(tournamentPlayers, dbPlayers) {
    const pool = (Array.isArray(dbPlayers) ? dbPlayers : []).filter(Boolean);
    const byId = new Map();
    const byFideId = new Map();
    const byName = new Map();
    pool.forEach(p => {
        byId.set(String(p.id), p);
        if (p.fideId) byFideId.set(String(p.fideId), p);
        if (p.name) byName.set(p.name.toLowerCase(), p);
    });

    const roster = (Array.isArray(tournamentPlayers) ? tournamentPlayers : []).filter(Boolean);

    // Each player's claims, at most one per strength.
    const claims = roster.map(tp => {
        const name = formatDbPlayerName(tp.lastName, tp.givenName);
        const fideId = tp.fideId ? String(tp.fideId) : '';
        const own = [];
        const linked = tp.dbPlayerId ? byId.get(String(tp.dbPlayerId)) : undefined;
        if (linked) {
            const strength = linkStrength(fideId, nameWords(name), linked);
            if (strength !== null) own.push({ strength, record: linked });
        }
        if (fideId && byFideId.has(fideId)) own.push({ strength: CLAIM.FIDE_ID, record: byFideId.get(fideId) });
        if (name && byName.has(name.toLowerCase())) own.push({ strength: CLAIM.NAME, record: byName.get(name.toLowerCase()) });
        return own;
    });

    const assigned = new Map();  // roster index -> record
    const closed = new Set();    // record ids already taken, or tied
    const lost = new Set();      // roster indexes that wanted a closed record
    Object.values(CLAIM).forEach(strength => {
        const wanted = new Map();  // record id -> roster indexes
        claims.forEach((own, i) => {
            if (assigned.has(i)) return;
            const claim = own.find(c => c.strength === strength);
            if (!claim) return;
            const id = String(claim.record.id);
            if (closed.has(id)) { lost.add(i); return; }
            if (!wanted.has(id)) wanted.set(id, []);
            wanted.get(id).push(i);
        });
        wanted.forEach((indexes, id) => {
            closed.add(id);
            if (indexes.length === 1) assigned.set(indexes[0], byId.get(id));
            else indexes.forEach(i => lost.add(i));
        });
    });

    const matched = [];
    const unmatched = [];
    const shared = [];
    roster.forEach((tp, i) => {
        if (assigned.has(i)) matched.push({ tournamentPlayer: tp, dbPlayer: assigned.get(i) });
        else if (lost.has(i)) shared.push(tp);
        else unmatched.push(tp);
    });

    const idMap = new Map();
    matched.forEach(m => idMap.set(String(m.tournamentPlayer.id), String(m.dbPlayer.id)));
    return { matched, unmatched, shared, idMap };
}

/** The matched players as the rating engine wants them, keyed by DATABASE id. */
export function buildEnginePlayers(matched, listId) {
    return (Array.isArray(matched) ? matched : []).map(m => ({
        id: String(m.dbPlayer.id),
        rating: readRatings(m.dbPlayer)[listId] || 0,
        games: gamesInList(m.dbPlayer, listId),
        birthYear: m.dbPlayer.birthYear,
        kFactor: ''
    }));
}

/**
 * The games to rate: only those where BOTH sides are in the database, rewritten
 * to database ids so no second mapping is needed when the report is applied.
 */
export function mapGamesToDbIds(games, idMap) {
    const map = idMap || new Map();
    return (Array.isArray(games) ? games : [])
        .filter(g => map.has(g.playerId) && map.has(g.opponentId))
        .map(g => ({
            playerId: map.get(g.playerId),
            opponentId: map.get(g.opponentId),
            score: g.score,
            isForfeit: g.isForfeit
        }));
}

/** The K-factor year, read off the tournament's ISO start date. */
export function eventYearFrom(startDate) {
    return parseInt(String(startDate || '').slice(0, 4), 10) || undefined;
}

// The first five names of a warning's players, trailing off after that.
function skippedNames(players) {
    return players.slice(0, 5).map(p => formatDbPlayerName(p.lastName, p.givenName)).join(', ')
        + (players.length > 5 ? '…' : '');
}

/**
 * What the preview warns about before anything is written. `shared` is
 * matchTournamentPlayers()'s list of players whose database record is someone
 * else's in this tournament too.
 */
export function reportWarnings(unmatched, report, shared) {
    const missing = Array.isArray(unmatched) ? unmatched : [];
    const sharing = Array.isArray(shared) ? shared : [];
    const warnings = [];
    if (missing.length > 0) {
        warnings.push(tr('{count} tournament players are not in this database and will be skipped: {names}', {
            count: missing.length,
            names: skippedNames(missing)
        }));
    }
    if (sharing.length > 0) {
        warnings.push(tr('{count} tournament players match the same database player as someone else in this tournament and will be skipped: {names}', {
            count: sharing.length,
            names: skippedNames(sharing)
        }));
    }
    const engineWarnings = (report && report.warnings) || [];
    if (engineWarnings.some(w => w && w.code === 'no-rated-opponents')) {
        warnings.push(tr('Some players had no rated opponents and are left unchanged.'));
    }
    return warnings;
}

/**
 * A rating change is a signed number the eye should be able to scan quickly:
 * always show the sign and use the locale's decimal comma. `tone` is what the
 * caller colours it with — 'none' for a change of nothing at all.
 */
export function formatSignedChange(change) {
    if (!change) return { text: '0', tone: 'none' };
    const rounded = Math.round(change * 10) / 10;
    const sign = rounded > 0 ? '+' : '';
    return {
        text: `${sign}${String(rounded).replace('.', ',')}`,
        tone: rounded > 0 ? 'up' : 'down'
    };
}

/** A number rounded to `places` decimals, written the Norwegian way. */
export function decimalComma(value, places = 1) {
    const factor = Math.pow(10, places);
    return String(Math.round(Number(value) * factor) / factor).replace('.', ',');
}

/**
 * Turn the endpoint's error codes into something an arbiter can act on. The one
 * that matters is 'stale-rating': it means someone else moved these ratings while
 * this preview was open, and re-previewing is the fix.
 */
export function describeReportError(result) {
    const r = result || {};
    if (r.error === 'already-applied') return tr('This tournament has already been reported to that list.');
    if (r.error === 'already-reverted') return tr('That report has already been reverted.');
    if (r.error === 'not-signed-in') return tr('You need to be signed in.');
    if (r.error === 'network') return tr('Could not reach the server. Check your connection and try again.');

    if (r.error === 'validation-failed' && Array.isArray(r.errors)) {
        if (r.errors.some(e => e && e.code === 'stale-rating')) {
            return tr('These ratings have changed since this preview was calculated — another report landed first. Close this and try again to recalculate.');
        }
        const first = r.errors[0];
        return tr('The report was rejected: {code}', { code: (first && first.code) || 'unknown' });
    }
    return tr('Something went wrong. Please try again.');
}

// ---------------------------------------------------------------------------
// Sharing a database
// ---------------------------------------------------------------------------

export function sharingErrorMessage(err) {
    return tr((err && err.message) || 'Something went wrong. Please try again.');
}

/**
 * Who a database is shared with, as rows. The owner is listed only to someone
 * else — on your own database you are not news — and only the owner gets the
 * buttons that change any of it.
 */
export function sharingRows(database, isOwner) {
    const d = database || {};
    const rows = [];
    if (!isOwner && d.ownerEmail) {
        rows.push({ email: d.ownerEmail, kind: 'owner', action: '', muted: false });
    }
    (d.cooperatingArbiters || []).forEach(email => {
        rows.push({ email, kind: 'arbiter', action: isOwner ? 'remove' : '', muted: false });
    });
    (d.pendingCooperatingArbiters || []).forEach(email => {
        rows.push({ email, kind: 'pending', action: isOwner ? 'retract' : '', muted: true });
    });
    return rows;
}

/** The notification types this page reacts to. */
export const DB_SHARE_TYPES = Object.freeze([
    'db_share_invite', 'db_share_accepted', 'db_share_declined', 'db_share_removed', 'db_deleted'
]);

/**
 * What a batch of notifications means for the page.
 *
 * Only NEW ones count: a "you were removed" notification sits in the bell until
 * it is dismissed, and must not act again on every later snapshot — least of all
 * on a database the arbiter has since been invited back into.
 *
 * `lost` means the database on screen was taken away, so the page should leave it
 * rather than letting the next read or write fail with a permission error;
 * `sharingChanged` means an owner with the sharing list open should see the answer
 * land without reopening it.
 */
export function classifyShareNotifications(notifications, seenIds, { activeDbId = '', isOwner = false } = {}) {
    const seen = seenIds || new Set();
    const fresh = (notifications || []).filter(n =>
        n && DB_SHARE_TYPES.includes(n.type) && !seen.has(n.id));

    const concerns = (n, types) =>
        types.includes(n.type) && String(n.databaseId) === String(activeDbId);

    return {
        fresh,
        lost: !!activeDbId && !isOwner
            && fresh.some(n => concerns(n, ['db_share_removed', 'db_deleted'])),
        sharingChanged: !!activeDbId && !!isOwner
            && fresh.some(n => concerns(n, ['db_share_accepted', 'db_share_declined']))
    };
}
